import React, {useCallback} from 'react';
import type {ClipboardEvent, ChangeEvent, ComponentProps} from 'react';
import {Textarea} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {
  getValue,
  getInputRef,
  getOnChange,
  getOnKeyDown,
  getIsLoading,
  getIsDisabled,
  useTextareaContext,
} from '../context';

const BorderlessTextarea = emotionStyled(Textarea)(
  {
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&[data-disabled="true"]': {
      // Keep the shell background visually uniform while uploads temporarily disable the input.
      opacity: 1,
      backgroundColor: 'transparent',
    },
    '& textarea': {
      padding: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
  },
  {
    variants: {
      hasTag: {
        true: {
          '& textarea': {paddingTop: '$0'},
        },
      },
    },
  }
);

type StyledTextareaProps = ComponentProps<typeof BorderlessTextarea>;

export interface InputProps extends Omit<
  StyledTextareaProps,
  | 'value'
  | 'onChange'
  | 'onKeyDown'
  | 'ref'
  | 'isDisabled'
  | 'isReadOnly'
  | 'hasTag'
  | 'onPaste'
> {
  hasTag?: boolean;
  isReadOnly?: boolean;
  requireShiftForNewLine?: boolean;
  onPaste?: (e: ClipboardEvent<HTMLTextAreaElement>) => Promise<void> | void;
}

export const TextareaInput = ({
  hasTag,
  onPaste,
  isReadOnly,
  requireShiftForNewLine,
  ...rest
}: InputProps) => {
  const value = useTextareaContext(getValue);
  const onChange = useTextareaContext(getOnChange);
  const inputRef = useTextareaContext(getInputRef);
  const onKeyDown = useTextareaContext(getOnKeyDown);
  const isLoading = useTextareaContext(getIsLoading);
  const isDisabled = useTextareaContext(getIsDisabled);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const handlePaste = useCallback(
    async (e: ClipboardEvent<HTMLTextAreaElement>) => {
      if (onPaste) {
        await onPaste(e);
      }
    },
    [onPaste]
  );

  return (
    <BorderlessTextarea
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      onPaste={handlePaste}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly || isLoading}
      hasTag={hasTag}
      shouldAutoSize
      requireShiftForNewLine={requireShiftForNewLine}
      {...rest}
    />
  );
};
